import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { Container, OverlayTrigger, Tooltip } from "react-bootstrap";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import PerfectScrollbar from "react-perfect-scrollbar";
import Countdown from "react-countdown";
import classNames from "classnames";
import ResearchUpgradeDialog from "../components/Dialogs/ResearchUpgradeDialog";
import ResearchTreeConnections, {
  TierConnection,
} from "../components/ResearchTreeConnections";
import Button from "../components/Button";
import CountUp from "react-countup";
import { CustomIcon } from "../components/Icons";
import { isMobile } from "react-device-detect";
import { useSelector, useDispatch } from "react-redux";
import { BuildingApiType } from "../types/buildings";
import BuildingService from "./village/BuildingService";
import Network from "../utils/Network";
import { setBuildingData } from "../redux/buildings/action";
import { setUserInfo } from "../redux/user/action";
import useToast from "../hooks/useToast";
import "./ResearchDetail.css";
import Dialog from "../components/Dialog";
import ThousandsSeparator from "../utils/thousands-separator";
import usePlayerTalents from "../hooks/usePlayerTalents";
import {
  getResearchCategoryBySlug,
  getResearchCategoryConfig,
  getResearchCategoryDescription,
  getResearchCategoryIcon,
  getResearchCategorySlug,
  findResearchCategoryBySlug,
  ResearchCategoryConfig,
} from "../constants/researchCategories";
import {
  PlayerTalent,
  UserTalentContract,
  UserTalentLevelContract,
} from "../types/talents";
import { ConfirmDialogType } from "../types/global";
import { useAppTranslation } from "../i18n/useAppTranslation";

type Props = {};

const RESEARCH_BUILDING_ID = 7;

const ResearchDetail = (props: Props) => {
  const { t } = useAppTranslation(["common", "research", "village"]);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const openToast = useToast();
  const axios = new Network();
  const { categoryId } = useParams<{ categoryId: string }>();
  const [activeTab, setActiveTab] = useState("owned");
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const tabContainerRef = useRef<HTMLDivElement>(null);
  const [selectedTalent, setSelectedTalent] = useState<PlayerTalent | null>(
    null
  );
  const [selectedNextLevel, setSelectedNextLevel] = useState<UserTalentLevelContract | undefined>(undefined);
  const [upgradeDisabledReason, setUpgradeDisabledReason] = useState<string | undefined>(undefined);
  const [upgradeDialogOpen, setUpgradeDialogOpen] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState<ConfirmDialogType>({
    open: false,
    msg: "",
  });
  
  // Research Building upgrade state
  const [researchBuilding, setResearchBuilding] = useState<any>(null);
  const [buildingUpgradeDialogOpen, setBuildingUpgradeDialogOpen] = useState(false);
  const [buildingUpgradeLoading, setBuildingUpgradeLoading] = useState(false);
  const [buildingConfirmDialog, setBuildingConfirmDialog] = useState<{
    open: boolean;
    title: string;
    msg: string;
    submit: (() => unknown) | null;
  }>({ open: false, title: "", msg: "", submit: null });
  
  const userInfo = useSelector((state: any) => state.userInfo);
  const buildingsData: BuildingApiType[] = useSelector((state: any) => state.buildings);
  
  const researchBuildingLevel = useMemo(() => {
    if (researchBuilding?.currentLevel !== undefined) {
      return researchBuilding.currentLevel;
    }
    const researchBuildingFromStore = buildingsData?.find((b) => b.buildingId === RESEARCH_BUILDING_ID);
    return researchBuildingFromStore?.currentLevel ?? 0;
  }, [researchBuilding, buildingsData]);
  
  const {
    talentTree,
    flattenedTalents,
    categories: talentCategories,
    upgradingTalent,
    loading,
    upgrading,
    upgradeTalentLevel,
    getTalentMaxLevel,
    getNextAvailableLevel,
    instantUpgradeTalentLevel,
  } = usePlayerTalents();
  const [isInstantUpgrading, setIsInstantUpgrading] = useState(false);

  const buildCategoryBranchSet = useCallback(
    (rootCategoryId: number): Set<number> => {
      const collectDescendants = (categoryId: number): number[] => {
        const children = talentCategories.filter(
          (cat) => cat.parentId === categoryId
        );

        if (!children.length) {
          return [];
        }

        return children.reduce<number[]>((acc, child) => {
          acc.push(child.id);
          acc.push(...collectDescendants(child.id));
          return acc;
        }, []);
      };

      return new Set<number>([
        rootCategoryId,
        ...collectDescendants(rootCategoryId),
      ]);
    },
    [talentCategories]
  );
  
  const countAvailableTalentsInBranch = useCallback((rootCategoryId: number): number => {
    const relevantCategoryIds = buildCategoryBranchSet(rootCategoryId);
    return flattenedTalents.filter((talent) => {
      if (!talent.categoryId || !relevantCategoryIds.has(talent.categoryId)) {
        return false;
      }
      const nextLevel = getNextAvailableLevel(talent.talentId);
      const requiredLevel = talent.researchBuildingRequiredLevel ?? 0;
      return !!nextLevel && requiredLevel <= researchBuildingLevel;
    }).length;
  }, [buildCategoryBranchSet, flattenedTalents, getNextAvailableLevel, researchBuildingLevel]);

  // Get the main category from API
  const mainCategory = useMemo(() => {
    if (!categoryId || !talentCategories.length) return undefined;

    return findResearchCategoryBySlug(talentCategories, categoryId);
  }, [categoryId, talentCategories]);

  // Get subcategories for this category
  const subcategories = useMemo(() => {
    if (!mainCategory) return [];
    return talentCategories.filter(cat => cat.parentId === mainCategory.id);
  }, [mainCategory, talentCategories]);

  const categoryConfig: ResearchCategoryConfig | undefined = useMemo(() => {
    if (mainCategory) {
      const baseConfig = getResearchCategoryConfig(
        mainCategory.id,
        mainCategory.name
      );
      return {
        ...baseConfig,
        slug: getResearchCategorySlug(mainCategory),
        title: mainCategory.name || baseConfig.title,
        icon: getResearchCategoryIcon(mainCategory) || baseConfig.icon,
        description:
          mainCategory.description ||
          baseConfig.description ||
          getResearchCategoryDescription(mainCategory),
      };
    }

    if (!categoryId) return undefined;

    const bySlug = getResearchCategoryBySlug(categoryId);
    if (bySlug) {
      return bySlug;
    }

    const numericId = Number(categoryId);
    if (!Number.isNaN(numericId)) {
      return getResearchCategoryConfig(numericId);
    }

    return undefined;
  }, [categoryId, mainCategory]);

  // Get talents for the active subcategory
  const talentsInCategory = useMemo(() => {
    if (!mainCategory) {
      return [] as PlayerTalent[];
    }

    // If there are subcategories, filter by the active subcategory
    if (subcategories.length > 0) {
      const activeSubcategoryId = subcategories[activeTabIndex]?.id;
      if (!activeSubcategoryId) return [];

      // Filter talents that belong to this subcategory (include deeper descendants)
      const relevantCategoryIds = buildCategoryBranchSet(activeSubcategoryId);
      const filtered = flattenedTalents.filter(
        (talent) =>
          !!talent.categoryId && relevantCategoryIds.has(talent.categoryId)
      );
      
      return filtered;
    }

    // If no subcategories, show all talents for the main category
    const relevantCategoryIds = buildCategoryBranchSet(mainCategory.id);
    const filtered = flattenedTalents.filter(
      (talent) =>
        !!talent.categoryId && relevantCategoryIds.has(talent.categoryId)
    );
    
    return filtered;
  }, [flattenedTalents, mainCategory, subcategories, activeTabIndex, buildCategoryBranchSet]);

  const { tieredTalents, tierConnections } = useMemo(() => {
    if (!talentsInCategory.length) {
      return {
        tieredTalents: [] as PlayerTalent[][],
        tierConnections: [] as TierConnection[][],
      };
    }

    if (!talentTree.length) {
      return {
        tieredTalents: [talentsInCategory],
        tierConnections: [ [] ],
      };
    }

    const relevantIds = new Set(talentsInCategory.map((talent) => talent.talentId));
    const talentMap = new Map(
      talentsInCategory.map((talent) => [talent.talentId, talent])
    );
    const adjacency = new Map<number, Set<number>>();
    const parentMap = new Map<number, number | null>();
    const rootCandidates = new Set<number>();

    const linkParent = (parentId: number, childId: number) => {
      if (!adjacency.has(parentId)) {
        adjacency.set(parentId, new Set());
      }
      adjacency.get(parentId)!.add(childId);
    };

    const visitTree = (node: UserTalentContract, lineageParentId?: number) => {
      const isRelevant = relevantIds.has(node.id);
      let nextParentId = lineageParentId;

      if (isRelevant) {
        const desiredParent =
          typeof lineageParentId === "number" ? lineageParentId : null;
        const currentParent = parentMap.get(node.id);
        const shouldSetParent =
          !parentMap.has(node.id) ||
          (currentParent === null && desiredParent !== null);

        if (shouldSetParent) {
          parentMap.set(node.id, desiredParent);
          if (desiredParent === null) {
            rootCandidates.add(node.id);
          } else {
            rootCandidates.delete(node.id);
            linkParent(desiredParent, node.id);
          }
        } else if (currentParent !== null) {
          linkParent(currentParent, node.id);
          rootCandidates.delete(node.id);
        } else {
          rootCandidates.add(node.id);
        }

        nextParentId = node.id;
      }

      node.children?.forEach((child) => visitTree(child, nextParentId));
    };

    talentTree.forEach((rootNode) => visitTree(rootNode));

    const sortByDisplayOrder = (a: number, b: number) => {
      const talentA = talentMap.get(a);
      const talentB = talentMap.get(b);
      if (talentA && talentB) {
        return talentA.name.localeCompare(talentB.name);
      }
      return a - b;
    };

    const tiers: PlayerTalent[][] = [];
    const queue: Array<{ id: number; depth: number }> = Array.from(
      rootCandidates
    )
      .sort(sortByDisplayOrder)
      .map((id) => ({ id, depth: 0 }));
    const visited = new Set<number>();

    while (queue.length) {
      const { id, depth } = queue.shift()!;
      if (visited.has(id) || !relevantIds.has(id)) {
        continue;
      }

      const talent = talentMap.get(id);
      if (!talent) {
        continue;
      }

      visited.add(id);

      if (!tiers[depth]) {
        tiers[depth] = [];
      }
      tiers[depth].push(talent);

      const children = Array.from(adjacency.get(id) || []).sort(
        sortByDisplayOrder
      );
      children.forEach((childId) => {
        queue.push({ id: childId, depth: depth + 1 });
      });
    }

    if (!tiers.length) {
      return {
        tieredTalents: [talentsInCategory],
        tierConnections: [ [] ],
      };
    }

    const positionMap = new Map<number, { tier: number; index: number }>();
    tiers.forEach((tierNodes, tierIndex) => {
      tierNodes.forEach((node, nodeIndex) => {
        positionMap.set(node.talentId, { tier: tierIndex, index: nodeIndex });
      });
    });

    const connections: TierConnection[][] = tiers.map(() => []);

    tiers.forEach((tierNodes, tierIndex) => {
      tierNodes.forEach((node) => {
        const children = Array.from(adjacency.get(node.talentId) || []);
        children.forEach((childId) => {
          const childPosition = positionMap.get(childId);
          const parentPosition = positionMap.get(node.talentId);
          if (
            childPosition &&
            parentPosition &&
            childPosition.tier === tierIndex + 1
          ) {
            // Get child talent to check if it's locked
            const childTalent = talentMap.get(childId);
            const childRequiredLevel = childTalent?.researchBuildingRequiredLevel ?? 0;
            const isChildLocked = childRequiredLevel > researchBuildingLevel;
            
            // Parent is "unlocked" if it has level > 0 (has been upgraded)
            const isParentUnlocked = node.level > 0;
            
            connections[tierIndex].push({
              fromIndex: parentPosition.index,
              toIndex: childPosition.index,
              id: `connection-${node.talentId}-${childId}`,
              isUnlocked: isParentUnlocked,
              isLocked: isChildLocked,
            });
          }
        });
      });
    });

    return {
      tieredTalents: tiers,
      tierConnections: connections,
    };
  }, [talentsInCategory, talentTree, researchBuildingLevel]);

  useEffect(() => {
    if (!mainCategory) {
      return;
    }

    // Reset to first tab when category changes
    setActiveTabIndex(0);
    if (subcategories.length > 0) {
      setActiveTab(`subcategory-${subcategories[0].id}`);
    } else {
      setActiveTab("all");
    }
  }, [mainCategory, subcategories]);

  const handleTabChange = (tabId: string, index: number) => {
    setActiveTab(tabId);
    setActiveTabIndex(index);
  };

  const handleOpenTalent = (talent: PlayerTalent, nextLevel?: UserTalentLevelContract) => {
    const requiredLevel = talent.researchBuildingRequiredLevel ?? 0;
    const buildingLocked = requiredLevel > researchBuildingLevel;
    let reason: string | undefined;

    if (buildingLocked) {
      reason = t("research:disabled.buildingLevelRequired", {
        required: requiredLevel,
        current: researchBuildingLevel,
      });
    } else if (!nextLevel) {
      const maxLevel = talent.maxLevel ?? getTalentMaxLevel(talent.talentId);
      reason =
        talent.level >= maxLevel
          ? t("research:disabled.maxLevelReached")
          : t("research:disabled.noUpgradeAvailable");
    }

    setSelectedTalent(talent);
    setSelectedNextLevel(nextLevel);
    setUpgradeDisabledReason(reason);
    setUpgradeDialogOpen(true);
  };

  const handleUpgradeSuccess = () => {
    setUpgradeDialogOpen(false);
    setSelectedTalent(null);
    setSelectedNextLevel(undefined);
    setUpgradeDisabledReason(undefined);
  };

  const handleDialogClose = () => {
    setUpgradeDialogOpen(false);
    setSelectedTalent(null);
    setSelectedNextLevel(undefined);
    setUpgradeDisabledReason(undefined);
  };

  const openInstantConfirm = () => {
    if (!upgradingTalent || !upgradingProgress) return;
    const cost = Math.max(
      0,
      Math.round(upgradingProgress.instantCost ?? 0)
    );
    setConfirmDialog({
      id: "instant-upgrade",
      open: true,
      title: t("village:dialogs.instantUpgrade.title"),
      msg: (
        <div className="text-center">
          <p className="mb-2">
            {t("research:instant.finishPrompt", {
              name: upgradingTalent.talentName,
            })}
          </p>
          <p className="mb-0">
            {t("common:labels.cost")}: <strong>{ThousandsSeparator(cost)}</strong>{" "}
            {t("village:resources.cleg")}
          </p>
        </div>
      ),
      submit: async () => {
        setIsInstantUpgrading(true);
        try {
          await instantUpgradeTalentLevel(upgradingTalent.levelId);
        } finally {
          setIsInstantUpgrading(false);
          setConfirmDialog((prev) => ({ ...prev, open: false }));
        }
      },
    });
  };

  const formatDuration = useCallback((ms: number): string => {
    const totalSeconds = Math.max(0, Math.floor(ms / 1000));
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    if (minutes <= 0) {
      return `${seconds}s`;
    }
    return `${minutes}m ${seconds.toString().padStart(2, "0")}s`;
  }, []);

  const upgradingProgress = useMemo(() => {
    if (!upgradingTalent) return null;

    const startTime = upgradingTalent.registerDateTime
      ? new Date(upgradingTalent.registerDateTime).getTime()
      : Date.now();
    const durationMs = (upgradingTalent.duration ?? 0) * 60 * 1000; // duration is in minutes
    const elapsedMs = Math.max(0, Date.now() - startTime);
    const remainingMs = Math.max(0, durationMs - elapsedMs);
    const progress =
      durationMs > 0 ? Math.min(100, (elapsedMs / durationMs) * 100) : 0;

    return {
      startTime,
      endTime: startTime + durationMs,
      durationMs,
      remainingMs,
      progress,
      instantCost:
        upgradingTalent.instantUpgradeCost ??
        upgradingTalent.upgradeCost ??
        0,
      imageUrl: flattenedTalents.find(t => t.talentId === upgradingTalent.talentId)?.imageUrl,
    };
  }, [upgradingTalent, flattenedTalents]);

  // Create tabs from subcategories
  const tabs = useMemo(() => {
    if (!mainCategory) {
      return [];
    }

    const fallbackIcon = getResearchCategoryIcon(mainCategory ?? categoryConfig);
    const fallbackDescription =
      categoryConfig?.description ||
      mainCategory.description ||
      getResearchCategoryDescription(mainCategory);

    // If there are subcategories, show them as tabs
    if (subcategories.length > 0) {
      return subcategories.map((subcat) => ({
        id: `subcategory-${subcat.id}`,
        title: subcat.name,
        icon: getResearchCategoryIcon(subcat) || fallbackIcon,
        count: countAvailableTalentsInBranch(subcat.id),
        description: subcat.description || fallbackDescription,
      }));
    }

    // If no subcategories, show a single "All" tab
    return [
      {
        id: "all",
        title: t("research:tabs.all"),
        icon: fallbackIcon,
        count: countAvailableTalentsInBranch(mainCategory.id),
        description: fallbackDescription,
      },
    ];
  }, [
    mainCategory,
    subcategories,
    categoryConfig,
    countAvailableTalentsInBranch,
  ]);

  // Fetch research building data
  const fetchResearchBuilding = useCallback(async () => {
    const apiBuilding = buildingsData?.find((b) => b.buildingId === RESEARCH_BUILDING_ID);
    if (apiBuilding && apiBuilding.id) {
      try {
        const response = await BuildingService.getBuildingDetail(apiBuilding.id);
        if ("ok" in response && response.ok && response.data) {
          setResearchBuilding({
            ...apiBuilding,
            ...response.data,
            buildingId: RESEARCH_BUILDING_ID,
          });
        }
      } catch (err) {
        console.error("Failed to fetch research building:", err);
      }
    }
  }, [buildingsData]);

  useEffect(() => {
    if (buildingsData?.length) {
      fetchResearchBuilding();
    }
  }, [buildingsData, fetchResearchBuilding]);

  // Refresh data helpers
  const refreshBuildings = async () => {
    try {
      const response = await BuildingService.getAllBuildings();
      if (response.success && response.data) {
        dispatch(setBuildingData(response.data));
      }
    } catch (err) {
      console.error("Failed to refresh buildings:", err);
    }
  };

  const refreshUserInfo = async () => {
    try {
      const response = await axios.CallApi({ url: "/player", method: "get" });
      if (response.ok) {
        dispatch(setUserInfo(response.data));
      }
    } catch (err) {
      console.error("Failed to refresh user info:", err);
    }
  };

  // Handle building upgrade button click
  const handleOpenBuildingUpgradeDialog = () => {
    if (researchBuilding) {
      setBuildingUpgradeDialogOpen(true);
    }
  };

  // Handle building upgrade confirmation
  const handleBuildingUpgrade = async () => {
    if (!researchBuilding?.id) return;

    setBuildingConfirmDialog({
      open: true,
      title: t("research:confirm.upgradeTitle"),
      msg: t("research:confirm.upgradeMessage", {
        level: (researchBuilding.currentLevel || 0) + 1,
      }),
      submit: async () => {
        setBuildingUpgradeLoading(true);
        try {
          const response = await BuildingService.upgradeBuilding(researchBuilding.id);
          openToast(response);
          if ("ok" in response && response.ok) {
            setBuildingConfirmDialog((prev) => ({ ...prev, open: false }));
            setBuildingUpgradeDialogOpen(false);
            await Promise.all([refreshBuildings(), refreshUserInfo()]);
            fetchResearchBuilding();
          }
        } catch (err) {
          console.error("Failed to upgrade research building:", err);
        } finally {
          setBuildingUpgradeLoading(false);
        }
      },
    });
  };

  return (
    <div className="research-detail-page">
      <Container className="py-4">
        {/* Header */}
        <div className="research-detail-header d-flex align-items-center justify-content-between mb-4">
          <div className="d-flex align-items-center gap-3">
            <img
              src="/assets/images/icons/back.svg"
              alt="Back"
              className="button"
              style={{ width: 53, height: 53 }}
              onClick={() => navigate("/research")}
            />
            <h2 className="research-title dark-yellow fs-2 fw-bold mb-0">
              {mainCategory?.name || categoryConfig?.title || t("research:title")}
            </h2>
          </div>
          <Button 
            variant="warning" 
            onClick={handleOpenBuildingUpgradeDialog}
            disabled={!researchBuilding}
          >
            {t("village:tabs.upgrade")}
          </Button>
        </div>

        {/* Tabs */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.4 }}
          className="tab-bar mt-4 mb-4"
        >
          <PerfectScrollbar style={{ maxWidth: "100%" }}>
            <div
              className="d-flex flex-nowrap m-0 p-0 justify-content-start justify-content-lg-center position-relative"
              ref={tabContainerRef}
            >
              {tabs.map((tab, index) => {
                const tabButton = (
                  <button
                    onClick={() => handleTabChange(tab.id, index)}
                    key={tab.id}
                    className={classNames(
                      "marketplace-tab pb-4 pt-3 position-relative px-md-4",
                      {
                        active: tab.id === activeTab,
                      }
                    )}
                    title={tab.description || tab.title}
                  >
                    <div className="b-text ps-1">
                      <span className="text-light fs-med d-flex flex-column align-items-center fs-lg fs-sm-small">
                        <img
                          className="mb-1 circle fs-med"
                          alt={tab.title}
                          src={tab.icon}
                          width="55px"
                          height="55px"
                        />
                        {tab.title}
                      </span>
                    </div>
                  </button>
                );

                if (tab.description) {
                  return (
                    <OverlayTrigger
                      key={tab.id}
                      placement="bottom"
                      overlay={<Tooltip id={`tab-tooltip-${tab.id}`}>{tab.description}</Tooltip>}
                    >
                      {tabButton}
                    </OverlayTrigger>
                  );
                }

                return tabButton;
              })}
            </div>
          </PerfectScrollbar>
        </motion.div>

        <motion.div
          className="p-relative"
          initial={isMobile ? undefined : { opacity: 0, y: -110, zIndex: 10 }}
          animate={{ opacity: 1, y: -18, zIndex: 10 }}
          transition={{ duration: 0.3, delay: 0, zIndex: 10 }}
          exit={{ opacity: 0, y: 130 }}
        >
          <div
            style={{ minHeight: 70, marginTop: -20 }}
            className="tab-bar gap-2 pt-md-3 pb-md-0 d-flex mb-4 py-4 pt-4 justify-content-between align-items-center flex-wrap "
          >
            <div className="d-flex gap-2 fs-sm-small  mt-2 mt-md-0 align-items-center">
              <CustomIcon icon="info" width={20} height={20} />
              Research Building helps you grow stronger over time by unlocking
              upgrades for combat, economy, and exploration.
            </div>
            <div className="d-flex ms-auto align-items-center">
              {/* <div className="status-frame d-flex align-items-center ms-3 ">
                <img
                  width={35}
                  height={40}
                  style={{ marginLeft: -5 }}
                  className="ms-n2"
                  src={"/assets/images/icons/zoom-scroll.png"}
                />
                <span
                  className="d-block"
                  style={{ fontSize: 18, marginRight: 0 }}
                >
                  <CountUp
                    duration={1}
                    decimals={0}
                    separator=","
                    end={userInfo?.totalBloodGem}
                  />
                </span>
                <img
                  width={30}
                  height={30}
                  src="/assets/images/buttons/add-button.png"
                />
              </div> */}

              <div className="status-frame d-flex align-items-center ms-3 ">
                <img
                  width={44}
                  height={40}
                  style={{ marginLeft: -15 }}
                  className="ms-n2"
                  src={"/assets/images/icons/cleg.png"}
                />
                <span
                  className="d-block"
                  style={{ fontSize: 18, marginRight: 0 }}
                >
                  <CountUp
                    duration={1}
                    decimals={0}
                    separator=","
                    end={userInfo?.totalCleg}
                  />
                </span>
                <Link to="/profile" className="button  mr-n2 p-0">
                  <img
                    width={30}
                    height={30}
                    src="/assets/images/buttons/add-button.png"
                  />
                </Link>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Upgrades Tree */}
        <div className="research-tree-container">
          {loading && (
            <div className="text-center text-white opacity-75 py-5">
              {t("research:empty.loadingData")}
            </div>
          )}
          
          {!loading && talentsInCategory.length === 0 && (
            <div className="text-center text-white opacity-75 py-5">
              <p>{t("research:empty.noTalentsInCategory")}</p>
              <p className="small text-muted">
                {t("research:empty.categoryStats", {
                  talents: flattenedTalents.length,
                  category: mainCategory?.name,
                  id: mainCategory?.id,
                  subcategories: subcategories.length,
                })}
              </p>
            </div>
          )}
          
          {/* Render each tier */}
          {!loading &&
            tieredTalents.map((tierTalents, tierIndex) => {
              const tierCount = tierTalents.length;
              const nextTierCount =
                tieredTalents[tierIndex + 1]?.length || 0;
              const tierConnectionSet = tierConnections[tierIndex] || [];
              const maxTierColumns = Math.max(tierCount, nextTierCount, 1);
              // Match CSS values: desktop 250px/30px, tablet 160px/12px, small mobile 140px/10px
              const screenWidth = typeof window !== 'undefined' ? window.innerWidth : 1024;
              const cardWidth = screenWidth <= 480 ? 140 : screenWidth <= 768 ? 160 : 250;
              const cardGap = screenWidth <= 480 ? 10 : screenWidth <= 768 ? 12 : 30;
              const minTierWidth = screenWidth <= 480 ? 300 : screenWidth <= 768 ? 360 : 480;
              const tierWidth =
                Math.max(
                  minTierWidth,
                  maxTierColumns * cardWidth +
                    Math.max(0, maxTierColumns - 1) * cardGap
                );
              const tierRowStyle = { minWidth: tierWidth };

              return (
                <div
                  key={`tier-${tierIndex}`}
                  className="tier-container"
                  style={{ ["--tier-width" as const]: `${tierWidth}px` }}
                >
                  {/* Upgrades Row */}
                  <div
                    className={classNames("tier-row ", {
                      "tier-single": tierCount === 1,
                      "tier-triple": tierCount === 3,
                    })}
                    style={tierRowStyle}
                  >
                    {tierTalents.map((talent) => {
                      const nextLevel = getNextAvailableLevel(talent.talentId);
                      const targetLevelId = nextLevel?.id ?? talent.talentLevelId;
                      const requiredLevel = talent.researchBuildingRequiredLevel ?? 0;
                      const isLockedByBuilding = requiredLevel > researchBuildingLevel;

                      // Check if this talent is currently being researched (in the queue)
                      const isCurrentlyResearching = upgradingTalent?.talentId === talent.talentId;

                      return (
                        <UpgradeCard
                          key={talent.talentId}
                          talent={talent}
                          onUpgrade={() => handleOpenTalent(talent, nextLevel)}
                          maxLevel={
                            talent.maxLevel ??
                            getTalentMaxLevel(talent.talentId)
                          }
                          canUpgrade={!!nextLevel && !isLockedByBuilding && !upgradingTalent}
                          isUpgrading={upgrading === targetLevelId}
                          isCurrentlyResearching={isCurrentlyResearching}
                          hasAnyUpgrading={!!upgradingTalent}
                          isLockedByBuilding={isLockedByBuilding}
                          requiredBuildingLevel={requiredLevel}
                          currentBuildingLevel={researchBuildingLevel}
                        />
                      );
                    })}
                  </div>
                  {/* Connection Lines */}
                  {nextTierCount > 0 && tierConnectionSet.length > 0 && (
                    <ResearchTreeConnections
                      currentTierCount={tierCount}
                      nextTierCount={nextTierCount}
                      connections={tierConnectionSet}
                      minWidth={tierWidth}
                      cardWidth={cardWidth}
                      cardGap={cardGap}
                    />
                  )}
                </div>
              );
            })}
        </div>

        {/* Bottom Progress Bar */}
        {upgradingTalent && upgradingProgress && (
          <Countdown
            key={upgradingTalent.levelId}
            date={upgradingProgress.endTime}
            overtime
            onComplete={() => {
              // Refetch talents when research completes
              setTimeout(() => {
                window.location.reload();
              }, 8000);
            }}
            renderer={({ total, completed }) => {
              const remainingMs = Math.max(0, total);
              const progress =
                upgradingProgress.durationMs > 0
                  ? Math.min(
                      100,
                      ((upgradingProgress.durationMs - remainingMs) /
                        upgradingProgress.durationMs) *
                        100
                    )
                  : 0;
              const label = completed
                ? t("research:progress.completed")
                : t("research:progress.timeLeft", {
                    duration: formatDuration(remainingMs),
                  });

              return (
                <div
                  className="research-progress-bar position-fixed bottom-0 start-0 w-100 p-3 py-2 d-flex align-items-center gap-3"
                  style={{ zIndex: 100 }}
                >
                  <img
                    src={upgradingProgress.imageUrl || "/assets/images/talents/default.png"}
                    alt={upgradingTalent.talentName}
                    style={{ width: 50, height: 50 }}
                  />
                  <div className="flex-grow-1 form-processbar">
                    <div className="d-flex justify-content-between mb-1">
                      <span className="text-white">
                        {t("research:progress.researching", {
                          name: upgradingTalent.talentName,
                        })}
                      </span>
                      <span className="text-white-50">{label}</span>
                    </div>
                    <div className="research-level-progress mx-1" style={{ maxWidth: 320 }}>
                      <div className="research-level-progress-frame p-1"  />
                      <div className="research-level-progress-track">
                        <div
                          className="research-level-progress-fill"
                          style={{ width: `${progress}%` }}
                        />
                      </div>
                      <span className="research-level-progress-text">
                        {Math.round(progress)}%
                      </span>
                    </div>
                  </div>
                  <Button
                    variant="wide-primary"
                    onClick={openInstantConfirm}
                    loading={isInstantUpgrading}
                    disabled={isInstantUpgrading}
                  >
                    {t("research:instant.action")}{" "}
                    <div className="p-1 bg-2 d-flex align-items-center gap-2 rounded ms-1 mt-1 ">
                      <CustomIcon icon="cleg" width={18} height={18} />{" "}
                      {ThousandsSeparator(
                        Math.max(
                          0,
                          Math.round(upgradingProgress.instantCost ?? 0)
                        )
                      )}
                    </div>
                  </Button>
                </div>
              );
            }}
          />
        )}
      </Container>

      <Dialog
        loading={isInstantUpgrading}
        title={confirmDialog.title}
        closeLabel={t("common:actions.no")}
        submitLabel={t("common:actions.yes")}
        onSubmit={() => confirmDialog.submit?.()}
        open={confirmDialog.open}
        onClose={() => setConfirmDialog((prev) => ({ ...prev, open: false }))}
      >
        {confirmDialog.msg}
      </Dialog>

      {/* Research Upgrade Dialog */}
      <ResearchUpgradeDialog
        open={upgradeDialogOpen}
        onClose={handleDialogClose}
        talent={selectedTalent}
        onUpgrade={(talentLevelId) => upgradeTalentLevel(talentLevelId)}
        nextLevel={selectedNextLevel ?? (selectedTalent ? getNextAvailableLevel(selectedTalent.talentId) : undefined)}
        canUpgrade={
          !!(
            selectedTalent &&
            !upgradeDisabledReason &&
            (selectedNextLevel ?? getNextAvailableLevel(selectedTalent.talentId))
          )
        }
        disabledReason={upgradeDisabledReason}
        onUpgradeSuccess={handleUpgradeSuccess}
        currentResearchBuildingLevel={researchBuildingLevel}
        parentTalent={selectedTalent?.parentTalentId ? flattenedTalents.find(t => t.talentId === selectedTalent.parentTalentId) : null}
        isCurrentlyUpgrading={!!(upgradingTalent && selectedTalent && upgradingTalent.talentId === selectedTalent.talentId)}
        upgradingProgress={upgradingTalent && selectedTalent && upgradingTalent.talentId === selectedTalent.talentId ? upgradingProgress : null}
        onInstantUpgrade={openInstantConfirm}
        activeResearchTalentId={upgradingTalent?.talentId ?? null}
        activeResearchTalentName={upgradingTalent?.talentName}
      />

      {/* Research Building Upgrade Dialog */}
      <Dialog
        open={buildingUpgradeDialogOpen}
        onClose={() => setBuildingUpgradeDialogOpen(false)}
        title={t("research:dialog.title")}
        noFooter
        size="lg"
      >
        {researchBuilding && (
          <div className="row">
            {/* Building image column */}
            <div className="col-12 col-lg-4 d-none d-sm-block">
              <div className="col-12 col-sm-8 offset-sm-2 col-lg-12 offset-lg-0">
                <div className="d-flex justify-content-center">
                  <div className="main-box-item text-light py-5">
                    <div className="mb-title text-center pt-3">
                      <h3 className="h6 fw-bold fs-med">
                        {t("research:dialog.progression.level")}
                        <button className="mb-title-btn fw-bold text-light mx-2" name="lblLevel">
                          {researchBuilding.currentLevel || 0}
                        </button>
                      </h3>
                    </div>
                    <div className="mb-body px-4 my-2">
                      <div className="image px-2 text-center">
                        <img
                          src="/assets/images/map/research.png"
                          className="img-fluid my-2"
                          alt="Research Building"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Building details column */}
            <div className="col-12 col-lg-8">
              <div className="row mb-0 mb-sm-2">
                <div style={{ flex: 1 }} className="col-12 col-sm-8 col-lg-9">
                  <div className="form-processbar fs-med">
                    <div className="mt-3">
                      <label className="d-flex justify-content-between mb-1">
                        <span>{t("village:sections.health")}</span>
                        <span>{researchBuilding.currentHealth || 100} / 100</span>
                      </label>
                      <div className="progress wide">
                        <div
                          className={`progress-bar progress-bar-striped ${
                            researchBuilding.currentHealth >= 80
                              ? "green"
                              : researchBuilding.currentHealth >= 60
                              ? "blue"
                              : researchBuilding.currentHealth >= 40
                              ? "orange"
                              : "red"
                          }`}
                          style={{ width: `${researchBuilding.currentHealth || 100}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tab Content */}
              <div className="row">
                <div className="col-12 position-relative">
                  <div className="tab-box-body p-3 p-lg-4 pb-lg-2">
                    <div className="land-info-list text-light fs-small mb-2">
                      <div className="land-info-item d-flex justify-content-between">
                        <div className="title fw-normal">
                          {t("research:dialog.currentLevel")}
                        </div>
                        <div className="value fw-bold">{researchBuilding.currentLevel || 0}</div>
                      </div>
                      <div className="land-info-item d-flex justify-content-between">
                        <div className="title fw-normal">
                          {t("research:dialog.unlockedTalents")}
                        </div>
                        <div className="value fw-bold">
                          {flattenedTalents.filter(t => 
                            !t.researchBuildingRequiredLevel || 
                            t.researchBuildingRequiredLevel <= (researchBuilding.currentLevel || 0)
                          ).length} / {flattenedTalents.length}
                        </div>
                      </div>
                    </div>

                    {researchBuilding.currentLevel < 8 && (
                      <div className="text-center">
                        <div className="d-flex flex-column align-items-center">
                          {researchBuilding.upgradeCost !== undefined && (
                            <div
                              style={{ marginBottom: 8 }}
                              className="d-flex mt-3 gap-2 flex-wrap align-items-end justify-content-center"
                            >
                              <div className="">{t("research:dialog.cost")}</div>
                              <div className="d-flex ms-2 gap-2 flex-wrap align-items-end">
                                <img src="/assets/images/icons/cleg.png" width={22} height={22} />
                                {researchBuilding.upgradeCost}
                              </div>
                            </div>
                          )}
                          <Button
                            onClick={handleBuildingUpgrade}
                            variant="wide-primary"
                            className="mt-3"
                            loading={buildingUpgradeLoading}
                            disabled={buildingUpgradeLoading}
                          >
                            <span>{t("research:dialog.upgradeResearch")}</span>
                          </Button>
                        </div>
                      </div>
                    )}

                    {researchBuilding.currentLevel >= 8 && (
                      <div className="text-center py-3">
                        <img src="/assets/images/icons/medal.png" alt="Max Level" width={50} height={50} />
                        <p className="text-success mt-2 mb-0 fw-bold">
                          {t("research:dialog.maxLevelReached")}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </Dialog>

      {/* Building Upgrade Confirm Dialog */}
      <Dialog
        open={buildingConfirmDialog.open}
        onClose={() => setBuildingConfirmDialog((prev) => ({ ...prev, open: false }))}
        title={buildingConfirmDialog.title}
        closeLabel={t("common:actions.no")}
        submitLabel={t("common:actions.yes")}
        onSubmit={() => buildingConfirmDialog.submit?.()}
        loading={buildingUpgradeLoading}
      >
        <p className="text-white text-center">{buildingConfirmDialog.msg}</p>
      </Dialog>
    </div>
  );
};

interface UpgradeCardProps {
  talent: PlayerTalent;
  onUpgrade: () => void;
  maxLevel: number;
  canUpgrade: boolean;
  isUpgrading: boolean;
  isCurrentlyResearching?: boolean;
  hasAnyUpgrading?: boolean;
  isLockedByBuilding?: boolean;
  requiredBuildingLevel?: number;
  currentBuildingLevel?: number;
}

const UpgradeCard: React.FC<UpgradeCardProps> = ({
  talent,
  onUpgrade,
  maxLevel,
  canUpgrade,
  isUpgrading,
  isCurrentlyResearching = false,
  hasAnyUpgrading = false,
  isLockedByBuilding = false,
  requiredBuildingLevel = 0,
  currentBuildingLevel = 0,
}) => {
  const level = talent.level;
  const progress = maxLevel > 0 ? Math.min(100, (level / maxLevel) * 100) : 0;
  const isCompleted = level >= maxLevel;
  const isApplied = talent.isActive || level > 0;
  const shouldShowProgress = level > 0 && maxLevel > 1;
  const lockReason = isLockedByBuilding
    ? `Research building Lv.${requiredBuildingLevel} required (current Lv.${currentBuildingLevel})`
    : !canUpgrade && !isCompleted
    ? "No upgrade available yet"
    : "";

  return (
    <div
      className={classNames("upgrade-card button position-relative", {
        "upgrade-locked":
          ((!canUpgrade && !isApplied && !isCompleted) || isLockedByBuilding) &&
          !isUpgrading && !isCurrentlyResearching,
        "upgrade-building-locked": isLockedByBuilding,
        "upgrade-active": isUpgrading || isCurrentlyResearching,
      })}
      title={lockReason || talent.description}
      onClick={onUpgrade}
    >
      {/* Lock badge for building requirement - minimal design */}
      {isLockedByBuilding && (
        <div className="upgrade-lock-badge">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="#FBD597">
            <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z"/>
          </svg>
          <span>Lv.{requiredBuildingLevel}</span>
        </div>
      )}

      <div className="upgrade-card-image-wrapper">
        <img
          src={talent.imageUrl || `/assets/images/talents/default.png`}
          alt={talent.name}
          className="upgrade-card-image"
        />
        {/* Lock overlay on image when locked */}
        {isLockedByBuilding && (
          <div className="upgrade-image-lock-overlay">
            <svg width="40" height="40" viewBox="0 0 24 24" fill="rgba(255, 255, 255, 0.7)">
              <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z"/>
            </svg>
          </div>
        )}
      </div>
      <div className="upgrade-card-footer">
        <h4 className="upgrade-card-title text-white fw-semibold mb-2 px-2">
          {talent.name}
        </h4>
        {isCompleted ? (
          <div className="upgrade-completed d-flex align-items-center justify-content-center gap-2">
            <img
              src="/assets/images/icons/medal.png"
              alt="Completed"
              style={{ width: 35, height: 35 }}
            />
            <span className="titlexv fw-bold fs-5 fs-sm-small">
              {level}/{maxLevel}
            </span>
          </div>
        ) : isLockedByBuilding ? (
          <div className="p-3 pt-0">
            <div className="research-level-progress small">
              <div className="research-level-progress-frame" />
              <div className="research-level-progress-track">
                <div
                  className="research-level-progress-fill"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <span className="research-level-progress-text">
                {level}/{maxLevel}
              </span>
            </div>
            <div className="text-center mt-2 upgrade-lock-reason">
              <small className="text-muted fs-small">Lv.{requiredBuildingLevel} required</small>
            </div>
          </div>
        ) : (
          <div className="p-3 pt-0">
            <div className="research-level-progress small">
              <div className="research-level-progress-frame" />
              <div className="research-level-progress-track">
                <div
                  className="research-level-progress-fill"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <span className="research-level-progress-text">
                {level}/{maxLevel}
              </span>
            </div>
          </div>
        )}
        {/* {canUpgrade && !isCompleted && !isLockedByBuilding && (
          <div className="d-flex justify-content-center">
            <Button
              variant="wide-primary"
              size="small"
              loading={isUpgrading}
              disabled={isUpgrading || hasAnyUpgrading}
              onClick={(event) => {
                event.stopPropagation();
                onUpgrade();
              }}
            >
              Upgrade
            </Button>
          </div>
        )} */}
      </div>
    </div>
  );
};

export default ResearchDetail;
